import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { HelmetProvider } from "react-helmet-async";
import AuthPage from "@/pages/AuthPage";
import WidgetDemo from "@/pages/WidgetDemo";
import LandingPage from "@/pages/LandingPage";
import ForgotPassword from "@/pages/ForgotPassword";
import MagicLogin from "@/pages/MagicLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import CookiePolicy from "@/pages/CookiePolicy";
import BlogList from "@/pages/BlogList";
import BlogPost from "@/pages/BlogPost";
import { Toaster } from "@/components/ui/sonner";
import SEOHead from "@/components/SEOHead";
import HreflangTags from "@/components/HreflangTags";
import CookieConsent from "@/components/CookieConsent";
import MiniSitePage from "@/pages/MiniSitePage";
import NotFoundPage from "@/pages/NotFoundPage";
import PartnerLanding from "@/pages/PartnerLanding";
import PartnerDashboard from "@/pages/PartnerDashboard";
import SupportPage from "@/pages/SupportPage";
import DataDeletion from "@/pages/DataDeletion";
import DPA from "@/pages/DPA";
import Subprocessors from "@/pages/Subprocessors";
import LegalNotice from "@/pages/LegalNotice";
import StaffJoinPage from "@/pages/StaffJoinPage";
import StaffDashboard from "@/pages/StaffDashboard";
import CalendarPage from "@/pages/CalendarPage";
import OnboardingPage from "@/pages/OnboardingPage";

// Use current origin for API calls to avoid hardcoded URLs
// In development (localhost:3000), use backend port 8001
// In production, use current origin (ingress handles /api routing)
const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || (isDevelopment ? 'http://localhost:8001' : window.location.origin);
export const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth (skip for public endpoints)
axios.interceptors.request.use(
  (config) => {
    // Skip auth for public endpoints
    const publicEndpoints = ['/widget/', '/chat/'];
    const isPublicEndpoint = publicEndpoints.some(endpoint => config.url?.includes(endpoint));
    
    if (!isPublicEndpoint) {
      const token = localStorage.getItem("token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Expired/invalid token → drop it and send the user to the login page
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const url = error?.config?.url || "";
    const isAuthCall = url.includes("/auth/") || url.includes("/widget/") || url.includes("/chat/");
    if (error?.response?.status === 401 && !isAuthCall && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      if (!window.location.pathname.startsWith("/auth")) window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);

// Already logged in → straight to the app (decided once on mount, so the login flow itself can still navigate freely)
function AuthRoute({ isAuthenticated, setIsAuthenticated }) {
  const [wasAuthed] = useState(isAuthenticated && !!localStorage.getItem("token"));
  if (wasAuthed) {
    let isAdmin = false;
    try { isAdmin = JSON.parse(localStorage.getItem("user") || "{}").is_admin === true; } catch (e) { /* ignore */ }
    return <Navigate to={isAdmin ? "/admin/dashboard" : "/dashboard"} replace />;
  }
  return <AuthPage setIsAuthenticated={setIsAuthenticated} />;
}

function DashboardRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!window.location.pathname.startsWith("/dashboard")) return;
    const sp = new URLSearchParams(window.location.search);
    const tab = sp.get("tab") || "settings";
    sp.delete("tab");
    sp.set("settings", tab === "billing" ? "subscription" : tab === "integrations" ? "widget" : tab);
    navigate(`/calendar?${sp.toString()}`, { replace: true });
  }, [navigate]);
  return null;
}

let _onboardingDoneFor = null; // token for which the funnel check already passed

function OnboardingGate() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    if (_onboardingDoneFor === token) return;
    if (location.pathname.startsWith("/onboarding")) return;
    const gated = ["/dashboard", "/calendar"];
    if (!gated.some((p) => location.pathname.startsWith(p))) return;
    axios.get(`${API}/onboarding/funnel`).then((r) => {
      if (r.data?.completed === false) {
        navigate("/onboarding");
      } else {
        _onboardingDoneFor = token;
      }
    }).catch(() => { _onboardingDoneFor = token; });
  }, [location.pathname, navigate]);
  return null;
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setIsAuthenticated(true);
    }
    setLoading(false);
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim().toLowerCase();
      if (ref) {
        localStorage.setItem('chatity_ref', ref);
        axios.post(`${API}/partner/track-click`, { ref_code: ref, landing: window.location.pathname }).catch(() => {});
      }
    } catch (e) { /* noop */ }
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white text-xl">Загрузка...</div>
    </div>;
  }

  return (
    <HelmetProvider>
      <div className="App">
        <Toaster position="top-right" />
        <BrowserRouter>
        <SEOHead />
        <HreflangTags />
        <CookieConsent />
        <OnboardingGate />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/auth" element={<AuthRoute isAuthenticated={isAuthenticated} setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/magic-login" element={<MagicLogin setIsAuthenticated={setIsAuthenticated} />} />
          <Route path="/dashboard" element={isAuthenticated ? <DashboardRedirect /> : <Navigate to="/auth" />} />
          <Route path="/onboarding" element={isAuthenticated ? <OnboardingPage /> : <Navigate to="/auth" />} />
          <Route path="/admin/dashboard" element={isAuthenticated ? <AdminDashboard setIsAuthenticated={setIsAuthenticated} /> : <Navigate to="/auth" />} />
          <Route path="/widget-demo/:salonId" element={<WidgetDemo />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/partner" element={<PartnerLanding />} />
          <Route path="/partner/dashboard" element={<PartnerDashboard />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/data-deletion" element={<DataDeletion />} />
          <Route path="/dpa" element={<DPA />} />
          <Route path="/subprocessors" element={<Subprocessors />} />
          <Route path="/legal-notice" element={<LegalNotice />} />
          <Route path="/mentions-legales" element={<LegalNotice />} />
          <Route path="/staff/join" element={<StaffJoinPage />} />
          <Route path="/staff" element={isAuthenticated ? <StaffDashboard /> : <Navigate to="/auth" />} />
          <Route path="/calendar" element={isAuthenticated ? <CalendarPage /> : <Navigate to="/auth" />} />
          {/* Public mini-site short links — must stay last before 404 */}
          <Route path="/:slug" element={<MiniSitePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;